import React, { useCallback, useEffect, useState } from "react";
import { adminLogin, getApplicants, exportCsv, updateApplicant } from "../api";
import ScoreEditor from "../components/ScoreEditor";
import Pagination from "../components/Pagination";
import AuditLogs from "../components/AuditLogs";
import { FiEye, FiEyeOff } from "react-icons/fi"; // 👈 added
import "../styles/AdminDashboard.css";

export default function AdminDashboard({ setAdminToken }) {
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false); // 👈 added
  const [applicants, setApplicants] = useState([]);
  const [filterEligible, setFilterEligible] = useState("all");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("applicants"); // "applicants" or "audit"
  const [auditStats, setAuditStats] = useState({ totalItems: 0, page: 1 }); // Audit log stats

  // Score editor modal state
  const [editingApplicant, setEditingApplicant] = useState(null);
  const [editingApplicantTotal, setEditingApplicantTotal] = useState(0);
  const [editingApplicantName, setEditingApplicantName] = useState("");
  const [editingApplicantScores, setEditingApplicantScores] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1); // 1-based
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [useServerPaging, setUseServerPaging] = useState(true); // attempt server paging by default

  useEffect(() => {
    const storedToken = localStorage.getItem("adminToken");
    if (storedToken) {
      setToken(storedToken); // Set token from local storage
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(username, password);
      const token = res.data.token;
      localStorage.setItem("adminToken", token);
      setToken(token);
      setAdminToken(token);
    } catch (err) {
      alert("Login failed");
    }
  };

  /**
   * loadApplicants
   * - Tries server-side paging when `useServerPaging` is true by passing page & limit to getApplicants.
   * - If server returns a plain array, falls back to client-side paging.
   */
  const loadApplicants = useCallback(
    async (pageNum = 1, limit = perPage) => {
      if (!token) return;

      setLoading(true);
      try {
        const eligibleParam =
          filterEligible === "all" ? undefined : filterEligible === "true";

        if (useServerPaging) {
          // ask server with page & limit (server may ignore these - we handle shapes below)
          const res = await getApplicants(token, eligibleParam, pageNum, limit);

          // Common server-side paged shape: { data: [...], meta: { total, page, limit } }
          if (res.data && Array.isArray(res.data.data) && res.data.meta) {
            setApplicants(res.data.data);
            setTotalItems(
              Number(
                res.data.meta.total ??
                  res.data.meta.totalItems ??
                  res.data.meta.count ??
                  0
              )
            );
            setPage(Number(res.data.meta.page ?? pageNum));
            setPerPage(Number(res.data.meta.limit ?? limit));
            return;
          }

          // Alternative shape: { items: [...], total: N }
          if (res.data && Array.isArray(res.data.items)) {
            setApplicants(res.data.items);
            setTotalItems(Number(res.data.total ?? res.data.meta?.total ?? 0));
            setPage(Number(pageNum));
            setPerPage(Number(limit));
            return;
          }

          // If server returned a plain array, switch to client-side mode and slice
          if (Array.isArray(res.data)) {
            const all = res.data;
            setUseServerPaging(false);
            setTotalItems(all.length);
            const start = (pageNum - 1) * limit;
            setApplicants(all.slice(start, start + limit));
            setPage(Number(pageNum));
            setPerPage(Number(limit));
            return;
          }

          // Unknown shape: try to find an array in res.data.data or fallback to empty
          const maybeArray = res.data?.data ?? res.data ?? [];
          if (Array.isArray(maybeArray)) {
            const all = maybeArray;
            setUseServerPaging(false);
            setTotalItems(all.length);
            const start = (pageNum - 1) * limit;
            setApplicants(all.slice(start, start + limit));
            setPage(Number(pageNum));
            setPerPage(Number(limit));
            return;
          }

          // Last fallback: empty
          setApplicants([]);
          setTotalItems(0);
          setPage(Number(pageNum));
          setPerPage(Number(limit));
        } else {
          // client-side: fetch all and slice locally
          const res = await getApplicants(token, eligibleParam);
          const all = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
          setTotalItems(all.length);
          const start = (pageNum - 1) * limit;
          setApplicants(all.slice(start, start + limit));
          setPage(Number(pageNum));
          setPerPage(Number(limit));
        }
      } catch (err) {
        alert("Failed to load");
      } finally {
        setLoading(false);
      }
    },
    [token, filterEligible, useServerPaging, perPage]
  );

  const downloadCsv = async () => {
    try {
      const res = await exportCsv(
        token,
        filterEligible === "all" ? undefined : filterEligible === "true"
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "applicants.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      alert("Export failed");
    }
  };

  // Open ScoreEditor modal (set name & scores if available)
  const updateScores = (id, currentTotal = 0, name = "", scores = null) => {
    setEditingApplicant(id);
    setEditingApplicantTotal(currentTotal || 0);
    setEditingApplicantName(name || "");
    setEditingApplicantScores(scores || null);
  };

  const updateScoresDirect = async (id, scores) => {
    try {
      await updateApplicant(token, id, { scores });
      // refresh current page after update
      await loadApplicants(page, perPage);
    } catch (err) {
      alert("Update failed");
    }
  };

  // Auto-load when token or filter changes (reset to page 1)
  useEffect(() => {
    if (!token) return;
    setPage(1);
    loadApplicants(1, perPage);
  }, [token, filterEligible, loadApplicants, perPage]);

  // Auto-load when page or perPage changes
  useEffect(() => {
    if (!token) return;
    loadApplicants(page, perPage);
  }, [page, perPage, loadApplicants, token]);

  /* ============
     BEFORE LOGIN
     -> Only show login card (no outer admin-card)
  ============ */
  if (!token) {
    return (
      <form className="admin-login" onSubmit={login}>
        <h3 className="section-title">Admin Login</h3>
        <div className="field">
          <input
            className="input"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        {/* 👇 Password field with eye icon */}
        <div className="field password-field">
          <input
            type={showPassword ? "text" : "password"}
            className="input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>

        <div className="actions">
          <button className="btn-primary">Login</button>
        </div>
      </form>
    );
  }

  /* ============
     AFTER LOGIN
     -> Outer admin-card + panel
  ============ */
  return (
    <div className="admin-card admin-card--after-login">
      <div className="admin-panel">
        {/* Header */}
        <header className="admin-header">
          <div>
            <h2 className="admin-title">Admin Dashboard</h2>
            <p className="admin-subtitle">
              Review applications, update scores, and export data.
            </p>
          </div>
          <div className="admin-summary">
            <span className="summary-pill">
              Total records:{" "}
              <strong>
                {activeTab === "applicants" ? totalItems : auditStats.totalItems}
              </strong>
            </span>
            <span className="summary-pill">
              Page:{" "}
              <strong>
                {activeTab === "applicants" ? page : auditStats.page}
              </strong>
            </span>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="admin-tabs">
          <button
            className={`tab-btn ${
              activeTab === "applicants" ? "active" : ""
            }`}
            onClick={() => setActiveTab("applicants")}
          >
            Applicants
          </button>
          <button
            className={`tab-btn ${activeTab === "audit" ? "active" : ""}`}
            onClick={() => setActiveTab("audit")}
          >
            Activity Log
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "applicants" && (
          <>
            {/* Controls */}
            <div className="admin-controls">
              <div className="select-wrap">
                <label className="select-label">Filter by Eligibility</label>
                <select
                  className="select"
                  value={filterEligible}
                  onChange={(e) => setFilterEligible(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="true">Eligible</option>
                  <option value="false">Not Eligible</option>
                </select>
              </div>

              <div className="btn-group">
                <button
                  className="btn-outline"
                  onClick={() => loadApplicants(1, perPage)}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Load Applicants"}
                </button>
                <button className="btn-success" onClick={downloadCsv}>
                  Download CSV
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="table-card">
              <div className="table-wrap">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Projects</th>
                      <th>Certifications</th>
                      <th>Qualification</th>
                      <th>Aggregate</th>
                      <th>Eligible</th>
                      <th>Total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applicants.map((a) => (
                      <tr key={a._id}>
                        <td className="mono" title={a.fullName}>
                          {a.fullName}
                        </td>
                        <td title={a.email}>{a.email}</td>
                        <td
                          className="muted"
                          title={
                            Array.isArray(a.projects)
                              ? a.projects
                                  .map((p) => p.title || p.link || "")
                                  .join(", ")
                              : ""
                          }
                        >
                          {Array.isArray(a.projects) && a.projects.length
                            ? a.projects
                                .map((p) => p.title || p.link || "")
                                .join(", ")
                            : "-"}
                        </td>
                        <td
                          className="muted"
                          title={
                            Array.isArray(a.certifications)
                              ? a.certifications
                                  .map((c) => c.name || "")
                                  .join(", ")
                              : ""
                          }
                        >
                          {Array.isArray(a.certifications) &&
                          a.certifications.length
                            ? a.certifications
                                .map((c) => c.name || "")
                                .join(", ")
                            : "-"}
                        </td>
                        <td>{a.qualification}</td>
                        <td style={{ textAlign: "right" }}>{a.aggregate}</td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className={`badge ${
                              a.eligible ? "badge--yes" : "badge--no"
                            }`}
                          >
                            {a.eligible ? "Yes" : "No"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {a.totalScore || 0}
                        </td>
                        <td className="actions-col">
                          <div className="actions-col-inner">
                            <button
                              className="tiny-btn"
                              onClick={() =>
                                updateScores(
                                  a._id,
                                  a.totalScore || 0,
                                  a.fullName,
                                  a.scores || null
                                )
                              }
                            >
                              Update
                            </button>

                            <a
                              className="tiny-btn outline"
                              href={
                                a.resumePath
                                  ? (process.env.REACT_APP_API_BASE ||
                                      "http://localhost:5000") + a.resumePath
                                  : "#"
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              Resume
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {applicants.length === 0 && (
                      <tr>
                        <td colSpan={9} className="empty">
                          No applicants loaded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination controls */}
            <div className="admin-footer">
              <Pagination
                totalItems={totalItems}
                currentPage={page}
                pageSize={perPage}
                onPageChange={(newPage) => {
                  setPage(newPage);
                }}
                onPageSizeChange={(newSize) => {
                  setPerPage(newSize);
                  setPage(1);
                }}
                pageSizeOptions={[5, 10, 20, 50]}
                siblingCount={1}
                boundaryCount={1}
                showJump={true}
              />

              <div className="admin-range">
                Showing{" "}
                <strong>
                  {totalItems === 0
                    ? 0
                    : Math.min((page - 1) * perPage + 1, totalItems)}
                </strong>{" "}
                – <strong>{Math.min(page * perPage, totalItems)}</strong> of{" "}
                <strong>{totalItems}</strong>
              </div>
            </div>
          </>
        )}

        {/* Audit Logs Tab */}
        {activeTab === "audit" && (
          <AuditLogs token={token} onStatsChange={setAuditStats} />
        )}
      </div>

      {editingApplicant && (
        <ScoreEditor
          token={token}
          applicantId={editingApplicant}
          applicantName={editingApplicantName}
          currentScores={editingApplicantScores}
          currentTotal={editingApplicantTotal}
          onClose={() => setEditingApplicant(null)}
          onSaved={async () => {
            await loadApplicants(page, perPage);
            setEditingApplicant(null);
          }}
        />
      )}
    </div>
  );
}
